import React, { useState, useEffect } from 'react';
import { AlertCircle, Shield, RefreshCw } from 'lucide-react';

// Import dashboard components
import AdminDashboard from './AdminDashboard';

// Placeholder dashboards (to be fully implemented)
const FinanceDashboard = React.lazy(() => import('./FinanceDashboard'));
const HRDashboard = React.lazy(() => import('./HRDashboard'));
const ReproDashboard = React.lazy(() => import('./ReproDashboard'));
const LabDashboard = React.lazy(() => import('./LabDashboard'));
const SalesDashboard = React.lazy(() => import('./SalesDashboard'));
const VetDashboard = React.lazy(() => import('./VetDashboard'));

// Role types
export type UserRole = 'admin' | 'finance' | 'hr' | 'repro' | 'lab' | 'sales' | 'vet';

// User interface
interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  permissions: string[];
  avatar?: string;
}

// Mock user service (replace with actual auth service)
const useCurrentUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Simulate API call to get current user
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mock user data - in real app, this would come from authentication service
        const mockUser: User = {
          id: 'user-123',
          name: 'Dr. Sarah Johnson',
          email: 'sarah.johnson@reprotech.com',
          role: 'admin', // Change this to test different dashboards
          permissions: ['read', 'write', 'admin'],
          avatar: undefined
        };
        
        setUser(mockUser);
      } catch (err) {
        setError('Failed to load user information');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const refreshUser = () => {
    setUser(null);
    setLoading(true);
    setError(null);
    // Trigger useEffect again
    const timer = setTimeout(() => {
      setLoading(false);
      setUser({
        id: 'user-123',
        name: 'Dr. Sarah Johnson',
        email: 'sarah.johnson@reprotech.com',
        role: 'admin',
        permissions: ['read', 'write', 'admin']
      });
    }, 500);
    
    return () => clearTimeout(timer);
  };

  return { user, loading, error, refreshUser };
};

// Dashboard router component
export const DashboardRouter: React.FC = () => {
  const { user, loading, error, refreshUser } = useCurrentUser();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Loading Dashboard</h2>
          <p className="text-sm text-gray-600">Please wait while we prepare your workspace...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Access Error</h2>
          <p className="text-sm text-gray-600 mb-6">
            {error || 'Unable to determine user role. Please contact support or try again.'}
          </p>
          <button
            onClick={refreshUser}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Render dashboard based on user role
  const renderDashboard = () => {
    switch (user.role) {
      case 'admin':
        return <AdminDashboard />;
        
      case 'finance':
        return (
          <React.Suspense fallback={<DashboardSkeleton />}>
            <FinanceDashboard />
          </React.Suspense>
        );
        
      case 'hr':
        return (
          <React.Suspense fallback={<DashboardSkeleton />}>
            <HRDashboard />
          </React.Suspense>
        );
        
      case 'repro':
        return (
          <React.Suspense fallback={<DashboardSkeleton />}>
            <ReproDashboard />
          </React.Suspense>
        );
        
      case 'lab':
        return (
          <React.Suspense fallback={<DashboardSkeleton />}>
            <LabDashboard />
          </React.Suspense>
        );
        
      case 'sales':
        return (
          <React.Suspense fallback={<DashboardSkeleton />}>
            <SalesDashboard />
          </React.Suspense>
        );
        
      case 'vet':
        return (
          <React.Suspense fallback={<DashboardSkeleton />}>
            <VetDashboard />
          </React.Suspense>
        );
        
      default:
        return <UnauthorizedDashboard userRole={user.role} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* User context provider could go here */}
      <div className="dashboard-container">
        {renderDashboard()}
      </div>
    </div>
  );
};

// Dashboard loading skeleton
const DashboardSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50">
    <div className="bg-white border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    </div>
    
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="animate-pulse space-y-8">
        {/* KPI Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="w-10 h-10 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Content Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Unauthorized dashboard
interface UnauthorizedDashboardProps {
  userRole: string;
}

const UnauthorizedDashboard: React.FC<UnauthorizedDashboardProps> = ({ userRole }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center max-w-md mx-auto p-6">
      <Shield className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h2>
      <p className="text-sm text-gray-600 mb-4">
        Your role "{userRole}" does not have access to any dashboard. 
        Please contact your administrator for assistance.
      </p>
      <p className="text-xs text-gray-500">
        If you believe this is an error, please log out and log back in with the correct credentials.
      </p>
    </div>
  </div>
);

export default DashboardRouter; 